import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ModalWrapper } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { ServiceFulfillmentPartner } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { client } from "@/hono";
import { EditContactModal } from "@/modals/contacts/edit-contact-modal";

export function RowActions({ cell }: CellContext<ServiceFulfillmentPartner, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const editContactModal = useDisclosure();
	const contactId = cell.row.original.contactId;
	const navigate = useNavigate();

	/** Functions */
	async function handleOnCompleteEdit() {
		await refetch?.();
	}

	function handleOnNavigateToContact() {
		const contactId = cell.row.original.contactId;
		if (!contactId) return;
		navigate(`/contacts/${contactId}`);
	}

	async function handleOnRemoveFulfillmentPartner() {
		if (!contactId) return;
		await client.organizations.services[":serviceId"].partners[":contactId"].$delete({
			param: {
				serviceId: cell.row.original.serviceId,
				contactId: cell.row.original.contactId,
			},
		});
		await refetch?.();
	}

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/contacts/${contactId}`}>
				<Menu
					items={() => [
						{
							label: t("general.open"),
							icon: icon("Info"),
							onClick: handleOnNavigateToContact,
							cyId: "row-actions-open-button",
						},
						{
							label: t("contacts.edit_contact"),
							onClick: editContactModal.onOpen,
							icon: icon("Edit"),
						},
						"divider",
						{
							label: t("organization.data.remove_fulfillment_partner"),
							onClick: handleOnRemoveFulfillmentPartner,
							icon: icon("Trash2"),
							isDanger: true,
						},
					]}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			<ModalWrapper>
				{editContactModal.isOpen && (
					<EditContactModal
						isOpen={editContactModal.isOpen}
						onClose={editContactModal.onClose}
						contactId={contactId}
						onComplete={handleOnCompleteEdit}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
